import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

const fmt = (n) => {
  const num = parseFloat(n) || 0;
  return num.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};

const newRow = () => ({ particulars: '', supplier: '', units: 0, rate: 0, cost: 0, repayment_period: 0, moratorium_months: 0 });

const SectionBadge = ({ code, isFinance }) => {
  const cls = isFinance
    ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
    : 'bg-violet-500/20 text-violet-300 border-violet-500/40';
  return <span className={`text-[11px] font-black px-2.5 py-0.5 rounded-lg border ${cls}`}>{code}</span>;
};

const TotalBadge = ({ label, value, highlight, isFinance }) => {
  let bg = 'bg-slate-800/60 border-slate-700';
  let text = 'text-white';
  let labelText = 'text-slate-400';
  if (highlight) {
    bg = 'bg-emerald-500/15 border-emerald-500/40';
    text = 'text-emerald-300';
    labelText = 'text-emerald-400';
  } else if (isFinance) {
    bg = 'bg-amber-500/10 border-amber-500/30';
    text = 'text-amber-300';
  }

  return (
    <div className={`flex items-center justify-between px-4 py-2 rounded-xl border ${bg}`}>
      <span className={`text-xs font-bold uppercase tracking-widest ${labelText}`}>{label}</span>
      <span className={`font-black font-mono text-sm ${text}`}>&#8377; {fmt(value)}</span>
    </div>
  );
};

const CostOfProject = ({ isEmbedded = false, selectedReportId = null }) => {
  const reportId = isEmbedded ? selectedReportId : null;

  const DEFAULT_SECTIONS = [
    { id: 'project_cost', title: 'PROJECT COST', isFinance: false, subHeads: [] },
    { id: 'working_capital', title: 'WORKING CAPITAL', isFinance: false, subHeads: [] },
    { id: 'means_of_finance', title: 'MEANS OF FINANCE', isFinance: true, subHeads: [] }
  ];

  const [sections, setSections] = useState(DEFAULT_SECTIONS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // UI Selection State
  const [selectedMainSectionId, setSelectedMainSectionId] = useState('project_cost');
  const [selectedSubHeadCode, setSelectedSubHeadCode] = useState(null);

  // Modals
  const [addMainSectionModal, setAddMainSectionModal] = useState(false);
  const [newMainSection, setNewMainSection] = useState({ name: '', isFinance: false });

  const [addHeadModal, setAddHeadModal] = useState(null); // stores sectionId
  const [newHead, setNewHead] = useState({ code: '', name: '' });

  const [deleteMainSectionModal, setDeleteMainSectionModal] = useState(null); // stores section object
  const [deleteHeadModal, setDeleteHeadModal] = useState(null); // { sectionId, subHeadIndex }
  const [confirmDeleteRow, setConfirmDeleteRow] = useState(null); // { sectionId, subHeadIndex, rowIndex }

  const loadData = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/cost-of-project/${reportId}`, { withCredentials: true });
      const data = res.data;
      if (!data || data.length === 0) {
        setSections(DEFAULT_SECTIONS);
        return;
      }

      const groupedByMain = {};
      data.forEach(r => {
        if (!groupedByMain[r.section_type]) groupedByMain[r.section_type] = [];
        groupedByMain[r.section_type].push(r);
      });

      const loadedSections = [];

      // Ensure defaults exist even if empty
      const typesPresent = Object.keys(groupedByMain);
      if (!typesPresent.includes('project_cost')) groupedByMain['project_cost'] = [];
      if (!typesPresent.includes('working_capital')) groupedByMain['working_capital'] = [];
      if (!typesPresent.includes('means_of_finance')) groupedByMain['means_of_finance'] = [];

      Object.keys(groupedByMain).forEach(stype => {
        let isFinance = stype === 'means_of_finance' || stype.startsWith('F_');
        let title = stype;
        if (stype === 'project_cost') title = 'PROJECT COST';
        else if (stype === 'working_capital') title = 'WORKING CAPITAL';
        else if (stype === 'means_of_finance') title = 'MEANS OF FINANCE';
        else if (stype.startsWith('C_')) title = stype.substring(2);
        else if (stype.startsWith('F_')) title = stype.substring(2);

        const subHeadsMap = new Map();
        groupedByMain[stype].forEach(r => {
          if (!subHeadsMap.has(r.section_code)) {
            subHeadsMap.set(r.section_code, { code: r.section_code, name: r.section_name, sort_order: r.sort_order, rows: [] });
          }
          subHeadsMap.get(r.section_code).rows.push({
            particulars: r.particulars, supplier: r.supplier,
            units: parseFloat(r.units) || 0, rate: parseFloat(r.rate) || 0, cost: parseFloat(r.cost) || 0,
            repayment_period: parseInt(r.repayment_period) || 0, moratorium_months: parseInt(r.moratorium_months) || 0,
            row_sort_order: r.row_sort_order
          });
        });

        const subHeads = Array.from(subHeadsMap.values()).sort((a, b) => a.sort_order - b.sort_order);
        subHeads.forEach(sh => sh.rows.sort((a, b) => a.row_sort_order - b.row_sort_order));

        loadedSections.push({
          id: stype,
          title: title.toUpperCase(),
          isFinance,
          subHeads
        });
      });

      // Sort main sections: PC, WC, MoF, then customs
      loadedSections.sort((a, b) => {
        const order = { 'project_cost': 1, 'working_capital': 2, 'means_of_finance': 3 };
        const oA = order[a.id] || 99;
        const oB = order[b.id] || 99;
        if (oA !== oB) return oA - oB;
        return a.title.localeCompare(b.title);
      });

      setSections(loadedSections);
    } catch { toast.error('Failed to load Cost of Project data'); }
    finally { setLoading(false); }
  }, [reportId]);

  useEffect(() => { loadData(); }, [loadData]);

  const calcCost = (u, r) => (parseFloat(u) || 0) * (parseFloat(r) || 0);

  const getSubHeadTotal = (subHead) => subHead.rows.reduce((s, r) => s + (r.cost || calcCost(r.units, r.rate)), 0);
  const getSectionTotal = (section) => section.subHeads.reduce((s, sh) => s + getSubHeadTotal(sh), 0);

  const totalCost = () => sections.filter(s => !s.isFinance).reduce((s, sec) => s + getSectionTotal(sec), 0);
  const totalFinance = () => sections.filter(s => s.isFinance).reduce((s, sec) => s + getSectionTotal(sec), 0);

  const addMainSection = () => {
    if (!newMainSection.name.trim()) { toast.error('Section name required'); return; }
    const id = newMainSection.isFinance ? `F_${newMainSection.name}` : `C_${newMainSection.name}`;
    if (sections.find(s => s.id.toUpperCase() === id.toUpperCase())) { toast.error('Section already exists'); return; }

    setSections(prev => [...prev, {
      id,
      title: newMainSection.name.toUpperCase(),
      isFinance: newMainSection.isFinance,
      subHeads: []
    }]);
    setNewMainSection({ name: '', isFinance: false });
    setAddMainSectionModal(false);
    toast.success('Main Section added');
  };

  const addSubHead = () => {
    if (!newHead.code.trim() || !newHead.name.trim()) { toast.error('Code and Name required'); return; }
    setSections(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const secIdx = next.findIndex(s => s.id === addHeadModal);
      if (secIdx === -1) return prev;

      if (next[secIdx].subHeads.find(sh => sh.code.toUpperCase() === newHead.code.toUpperCase())) {
        toast.error('Code already exists in this section');
        return prev;
      }

      next[secIdx].subHeads.push({
        code: newHead.code.toUpperCase(),
        name: newHead.name.trim(),
        rows: [newRow()] // Start with one empty row
      });
      return next;
    });
    setNewHead({ code: '', name: '' });
    setAddHeadModal(null);
    toast.success('Sub-head added');
  };

  const removeSubHead = () => {
    setSections(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const secIdx = next.findIndex(s => s.id === deleteHeadModal.sectionId);
      if (secIdx !== -1) {
        next[secIdx].subHeads.splice(deleteHeadModal.subHeadIndex, 1);
      }
      return next;
    });
    setDeleteHeadModal(null);
    toast.success('Sub-head removed');
  };

  const removeMainSection = () => {
    setSections(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const secIdx = next.findIndex(s => s.id === deleteMainSectionModal.id);
      if (secIdx !== -1) {
        next.splice(secIdx, 1);
      }
      return next;
    });
    // If the deleted section was selected, clear selection
    if (selectedMainSectionId === deleteMainSectionModal.id) {
      setSelectedMainSectionId(null);
      setSelectedSubHeadCode(null);
    }
    setDeleteMainSectionModal(null);
    toast.success('Main section removed');
  };

  const updateRow = (secId, shIdx, rIdx, field, val) => {
    setSections(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const secIdx = next.findIndex(s => s.id === secId);
      if (secIdx === -1) return prev;
      const row = next[secIdx].subHeads[shIdx].rows[rIdx];
      row[field] = val;
      if (field === 'units' || field === 'rate') {
        row.cost = calcCost(field === 'units' ? val : row.units, field === 'rate' ? val : row.rate);
      }
      return next;
    });
  };

  const addRow = (secId, shIdx) => {
    setSections(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const secIdx = next.findIndex(s => s.id === secId);
      if (secIdx !== -1) {
        next[secIdx].subHeads[shIdx].rows.push(newRow());
      }
      return next;
    });
  };

  const removeRow = () => {
    setSections(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      const secIdx = next.findIndex(s => s.id === confirmDeleteRow.sectionId);
      if (secIdx !== -1) {
        const rows = next[secIdx].subHeads[confirmDeleteRow.subHeadIndex].rows;
        if (rows.length <= 1) {
          next[secIdx].subHeads[confirmDeleteRow.subHeadIndex].rows = [newRow()];
        } else {
          rows.splice(confirmDeleteRow.rowIndex, 1);
        }
      }
      return next;
    });
    setConfirmDeleteRow(null);
  };

  const handleSave = async () => {
    if (!reportId) { toast.error('No report selected'); return; }
    setSaving(true);
    try {
      const rows = [];
      sections.forEach((sec) => {
        sec.subHeads.forEach((sh, hi) => {
          sh.rows.forEach((r, ri) => {
            rows.push({
              section_type: sec.id,
              section_code: sh.code,
              section_name: sh.name,
              particulars: r.particulars,
              supplier: r.supplier,
              units: r.units,
              rate: r.rate,
              cost: r.cost || calcCost(r.units, r.rate),
              sort_order: hi,
              row_sort_order: ri,
              repayment_period: r.repayment_period || 0,
              moratorium_months: r.moratorium_months || 0
            });
          });
        });
      });
      await axios.post(`${process.env.REACT_APP_API_URL}/api/cost-of-project/${reportId}`, { rows }, { withCredentials: true });
      toast.success('Cost of Project saved!');
    } catch (err) { toast.error('Save failed: ' + (err.response?.data?.error || err.message)); }
    finally { setSaving(false); }
  };

  const balanceDiff = Math.abs(totalCost() - totalFinance());
  const isBalanced = balanceDiff < 1;

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="w-8 h-8 rounded-full border-4 border-violet-500/20 border-t-violet-400 animate-spin mr-3" />
      <span className="text-violet-400 font-bold text-sm">Loading Cost of Project...</span>
    </div>
  );

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-500/20 border border-violet-500/30 flex items-center justify-center text-violet-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div>
            <h2 className="font-black text-white text-lg">COST OF THE PROJECT</h2>
            <p className="text-xs text-violet-400/70">Capital sheet — Dynamic project investment breakdown</p>
          </div>
        </div>
        <button onClick={handleSave} disabled={saving} className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-bold py-2.5 px-6 rounded-xl transition flex items-center gap-2 text-sm shadow-lg shadow-violet-600/25 disabled:opacity-50">
          {saving ? (
            <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Saving...</>
          ) : (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>Save All</>
          )}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="border rounded-xl p-4 border-violet-500/30 bg-violet-500/10 text-violet-300">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Total Project Cost</p>
          <p className="font-black font-mono text-lg">&#8377; {fmt(totalCost())}</p>
        </div>
        <div className="border rounded-xl p-4 border-amber-500/30 bg-amber-500/10 text-amber-300">
          <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Total Finance</p>
          <p className="font-black font-mono text-lg">&#8377; {fmt(totalFinance())}</p>
        </div>
        <div className={`col-span-2 border rounded-xl p-4 flex items-center justify-between ${isBalanced ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-rose-500/30 bg-rose-500/10 text-rose-300'}`}>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-widest opacity-60 mb-1">Balance Status</p>
            <p className="font-black text-sm">{isBalanced ? 'Perfectly Balanced' : `Shortfall/Excess of ₹ ${fmt(balanceDiff)}`}</p>
          </div>
          {isBalanced ? (
            <svg className="w-8 h-8 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          ) : (
            <svg className="w-8 h-8 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
          )}
        </div>
      </div>

      {/* 3-COLUMN DRILL DOWN UI */}
      <div className="flex gap-4 min-h-[500px] h-[calc(100vh-220px)] w-full">
        {/* PANE 1: Main Sections */}
        <div className="w-[300px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-full">
          <div className="p-4 bg-slate-800/80 border-b border-slate-700">
            <h3 className="text-white font-bold text-sm uppercase tracking-wider">Level 1 (Main Section)</h3>
            <p className="text-xs text-slate-400 mt-1">{sections.length} items</p>
          </div>
          <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
            {sections.map((sec, idx) => {
              const isSelected = selectedMainSectionId === sec.id;
              const isFin = sec.isFinance;
              const textClass = isFin ? 'text-amber-300' : 'text-violet-300';
              return (
                <div key={sec.id} onClick={() => { setSelectedMainSectionId(sec.id); setSelectedSubHeadCode(null); }} className={`cursor-pointer p-3 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? (isFin ? 'bg-amber-500/10 border-amber-500 shadow-amber-500/20' : 'bg-violet-500/10 border-violet-500 shadow-violet-500/20') : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 overflow-hidden">
                      <SectionBadge code={idx + 1} isFinance={isFin} />
                      <h4 className={`font-bold text-sm truncate ${isSelected ? textClass : 'text-slate-200'}`}>{sec.title}</h4>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); setDeleteMainSectionModal(sec); }} className="text-rose-500/60 hover:text-rose-400 p-1 rounded hover:bg-rose-500/10 transition shrink-0 opacity-0 group-hover:opacity-100">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className={`text-xs font-mono font-bold text-right ${isSelected ? textClass : 'text-slate-400'}`}>
                    &#8377; {fmt(getSectionTotal(sec))}
                  </div>
                </div>
              );
            })}
          </div>
          <div className="p-3 bg-slate-800/50 border-t border-slate-700">
            <button onClick={() => setAddMainSectionModal(true)} className="w-full bg-slate-700 hover:bg-slate-600 text-white text-xs font-bold py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-sm">
              <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
              New Section
            </button>
          </div>
        </div>

        {/* PANE 2: Sub Heads */}
        {(() => {
          const activeSec = sections.find(s => s.id === selectedMainSectionId);
          if (!activeSec) return null;
          const isFin = activeSec.isFinance;
          return (
            <div className="w-[320px] flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner shrink-0 h-fit max-h-full">
              <div className="p-4 bg-slate-800/80 border-b border-slate-700">
                <h3 className="text-white font-bold text-sm uppercase tracking-wider">Level 2 (Sub-Heads)</h3>
                <p className="text-xs text-slate-400 mt-1">{activeSec.subHeads.length} items</p>
              </div>
              <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-2">
                {activeSec.subHeads.length === 0 ? (
                  <div className="text-center py-10 text-slate-500 text-xs italic">No sub-heads yet</div>
                ) : activeSec.subHeads.map((sh, idx) => {
                  const isSelected = selectedSubHeadCode === sh.code;
                  return (
                    <div key={sh.code} onClick={() => setSelectedSubHeadCode(sh.code)} className={`cursor-pointer p-3 rounded-xl border transition-all select-none group flex flex-col gap-2 shadow-sm ${isSelected ? 'bg-fuchsia-500/10 border-fuchsia-500 shadow-fuchsia-500/20' : 'bg-slate-800 border-slate-700 hover:border-slate-500 hover:bg-slate-700/80'}`}>
                      <div className="flex items-center gap-2 justify-between">
                        <div className="flex items-center gap-2 overflow-hidden">
                          <SectionBadge code={sh.code} isFinance={isFin} />
                          <h4 className={`font-bold text-sm truncate ${isSelected ? 'text-fuchsia-300' : 'text-slate-200'}`}>{sh.name}</h4>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setDeleteHeadModal({ sectionId: activeSec.id, subHeadIndex: idx, name: sh.name }); }} className="text-rose-500/60 hover:text-rose-400 p-1 rounded hover:bg-rose-500/10 transition shrink-0 opacity-0 group-hover:opacity-100">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                      </div>
                      <div className={`text-xs font-mono font-bold text-right ${isSelected ? 'text-fuchsia-300' : 'text-slate-400'}`}>
                        &#8377; {fmt(getSubHeadTotal(sh))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="p-3 bg-slate-800/50 border-t border-slate-700">
                <button onClick={() => { setNewHead({ code: '', name: '' }); setAddHeadModal(activeSec.id); }} className={`w-full text-white text-xs font-bold py-2.5 rounded-lg transition flex items-center justify-center gap-2 shadow-sm ${isFin ? 'bg-amber-600 hover:bg-amber-500' : 'bg-violet-600 hover:bg-violet-500'}`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                  New Sub-Head
                </button>
              </div>
            </div>
          );
        })()}

        {/* PANE 3: Table */}
        {(() => {
          const activeSec = sections.find(s => s.id === selectedMainSectionId);
          if (!activeSec) return null;
          const activeShIdx = activeSec.subHeads.findIndex(sh => sh.code === selectedSubHeadCode);
          if (activeShIdx === -1) {
            return (
              <div className="flex-1 flex flex-col items-center justify-center bg-slate-900/50 border border-slate-700/50 rounded-2xl h-fit max-h-full min-h-[300px]">
                <svg className="w-16 h-16 text-slate-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                <p className="text-slate-400 text-sm">Select a sub-head to view and edit its rows</p>
              </div>
            );
          }
          const sh = activeSec.subHeads[activeShIdx];
          const isFin = activeSec.isFinance;

          return (
            <div className="flex-1 flex flex-col bg-slate-900/50 border border-slate-700/50 rounded-2xl overflow-hidden shadow-inner h-fit max-h-full">
              <div className="p-4 bg-slate-800/80 border-b border-slate-700 flex items-center justify-between">
                <div>
                  <h3 className="text-white font-bold text-sm uppercase tracking-wider">Level 3 (Table Data)</h3>
                  <p className="text-xs text-slate-400 mt-1">{sh.name}</p>
                </div>
                <div className={`text-sm font-mono font-bold px-3 py-1.5 rounded-lg border ${isFin ? 'text-amber-400/90 bg-amber-500/10 border-amber-500/20' : 'text-violet-400/90 bg-violet-500/10 border-violet-500/20'}`}>
                  Sub-Total: &#8377; {fmt(getSubHeadTotal(sh))}
                </div>
              </div>
              <div className="flex-1 overflow-x-auto overflow-y-auto custom-scrollbar">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-slate-800 border-b border-slate-700 text-[10px] uppercase tracking-widest text-slate-400 z-10 shadow-sm">
                    <tr>
                      <th className="py-3 px-3 text-left font-bold" style={{ minWidth: '180px' }}>Particulars</th>
                      {!isFin && <th className="py-3 px-3 text-left font-bold" style={{ minWidth: '130px' }}>Supplier</th>}
                      {!isFin && <th className="py-3 px-3 text-right font-bold" style={{ minWidth: '80px' }}>Units</th>}
                      {!isFin && <th className="py-3 px-3 text-right font-bold" style={{ minWidth: '100px' }}>Rate</th>}

                      {isFin && <th className="py-3 px-3 text-right font-bold" style={{ minWidth: '150px' }}>Repayment (m)</th>}
                      {isFin && <th className="py-3 px-3 text-right font-bold" style={{ minWidth: '150px' }}>Moratorium (m)</th>}

                      <th className="py-3 px-3 text-right font-bold" style={{ minWidth: '130px' }}>{isFin ? 'Amount' : 'Cost'}</th>
                      <th className="py-3 px-2 text-center w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sh.rows.map((row, rIdx) => (
                      <tr key={rIdx} className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors group">
                        <td className="px-2 py-2"><input type="text" value={row.particulars} onChange={e => updateRow(activeSec.id, activeShIdx, rIdx, 'particulars', e.target.value)} placeholder="Particulars..." className="w-full bg-slate-950/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-fuchsia-500 transition placeholder-slate-600" /></td>

                        {!isFin && <td className="px-2 py-2"><input type="text" value={row.supplier} onChange={e => updateRow(activeSec.id, activeShIdx, rIdx, 'supplier', e.target.value)} placeholder="Supplier" className="w-full bg-slate-950/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-white text-sm outline-none focus:border-fuchsia-500 transition placeholder-slate-600" /></td>}
                        {!isFin && <td className="px-2 py-2"><input type="number" min="0" step="any" value={row.units === 0 ? '' : row.units} onChange={e => updateRow(activeSec.id, activeShIdx, rIdx, 'units', parseFloat(e.target.value) || 0)} placeholder="0" className="w-full bg-slate-950/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-white text-sm text-right font-mono outline-none focus:border-fuchsia-500 transition placeholder-slate-600" /></td>}
                        {!isFin && <td className="px-2 py-2"><input type="number" min="0" step="any" value={row.rate === 0 ? '' : row.rate} onChange={e => updateRow(activeSec.id, activeShIdx, rIdx, 'rate', parseFloat(e.target.value) || 0)} placeholder="0" className="w-full bg-slate-950/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-white text-sm text-right font-mono outline-none focus:border-fuchsia-500 transition placeholder-slate-600" /></td>}

                        {isFin && <td className="px-2 py-2"><input type="number" min="0" step="1" value={row.repayment_period === 0 ? '' : row.repayment_period} onChange={e => updateRow(activeSec.id, activeShIdx, rIdx, 'repayment_period', parseInt(e.target.value) || 0)} placeholder="0" className="w-full bg-slate-950/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-white text-sm text-right font-mono outline-none focus:border-fuchsia-500 transition placeholder-slate-600" /></td>}
                        {isFin && <td className="px-2 py-2"><input type="number" min="0" step="1" value={row.moratorium_months === 0 ? '' : row.moratorium_months} onChange={e => updateRow(activeSec.id, activeShIdx, rIdx, 'moratorium_months', parseInt(e.target.value) || 0)} placeholder="0" className="w-full bg-slate-950/50 border border-slate-700/50 rounded-lg px-3 py-1.5 text-white text-sm text-right font-mono outline-none focus:border-fuchsia-500 transition placeholder-slate-600" /></td>}

                        <td className="px-2 py-2">
                          <input type="number" min="0" step="any" value={row.cost === 0 ? '' : row.cost} onChange={e => updateRow(activeSec.id, activeShIdx, rIdx, 'cost', parseFloat(e.target.value) || 0)} placeholder="0" className={`w-full bg-slate-950 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-right font-mono font-bold outline-none focus:border-fuchsia-500 transition placeholder-slate-600 ${isFin ? 'text-amber-300' : 'text-violet-300'}`} />
                        </td>

                        <td className="px-2 py-2 text-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => setConfirmDeleteRow({ sectionId: activeSec.id, subHeadIndex: activeShIdx, rowIndex: rIdx })} className="text-rose-500 hover:text-rose-400 p-1 rounded hover:bg-rose-500/10 transition">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="p-3 bg-slate-800/50 border-t border-slate-700">
                <button onClick={() => addRow(activeSec.id, activeShIdx)} className={`text-xs font-bold px-4 py-2.5 rounded-lg border transition flex items-center justify-center gap-1.5 w-full ${isFin ? 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10' : 'border-violet-500/30 text-violet-400 hover:bg-violet-500/10'}`}>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                  Add New Row
                </button>
              </div>
            </div>
          );
        })()}
      </div>

      <style>{`.custom-scrollbar::-webkit-scrollbar { width: 6px; height: 6px; } .custom-scrollbar::-webkit-scrollbar-track { background: transparent; } .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; } .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }`}</style>


      {/* ADD MAIN SECTION MODAL */}
      {addMainSectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={e => e.target === e.currentTarget && setAddMainSectionModal(false)}>
          <div className="bg-slate-900 border border-emerald-500/30 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-emerald-500/10 border-b border-emerald-500/20 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" /></svg>
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">New Main Section</h3>
                  <p className="text-xs text-slate-400">Create a custom root category</p>
                </div>
              </div>
              <button onClick={() => setAddMainSectionModal(false)} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Section Name *</label>
                <input type="text" value={newMainSection.name} onChange={e => setNewMainSection(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Preliminary Expenses" autoFocus className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white text-sm outline-none focus:border-emerald-500 transition placeholder-slate-600" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Section Type</label>
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => setNewMainSection(p => ({ ...p, isFinance: false }))} className={`py-2.5 rounded-xl border text-sm font-bold transition ${!newMainSection.isFinance ? 'bg-violet-500/20 border-violet-500 text-violet-300' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>Cost (Add to Total)</button>
                  <button onClick={() => setNewMainSection(p => ({ ...p, isFinance: true }))} className={`py-2.5 rounded-xl border text-sm font-bold transition ${newMainSection.isFinance ? 'bg-amber-500/20 border-amber-500 text-amber-300' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>Finance (For Balancing)</button>
                </div>
              </div>
              <div className="flex gap-3 pt-2 border-t border-slate-800">
                <button onClick={() => setAddMainSectionModal(false)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-xl transition text-sm">Cancel</button>
                <button onClick={addMainSection} className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-3 rounded-xl transition text-sm">Create Section</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ADD SUB-HEAD MODAL */}
      {addHeadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={e => e.target === e.currentTarget && setAddHeadModal(null)}>
          <div className="bg-slate-900 border border-violet-500/30 rounded-3xl w-full max-w-md shadow-2xl overflow-hidden">
            <div className="bg-violet-500/10 border-b border-violet-500/20 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-violet-500/20 text-violet-400 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" /></svg>
                </div>
                <div>
                  <h3 className="font-bold text-white text-lg">Add Sub-Head</h3>
                  <p className="text-xs text-slate-400">New category inside section</p>
                </div>
              </div>
              <button onClick={() => setAddHeadModal(null)} className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-slate-700 transition">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Sub-Head Code *</label>
                <input type="text" value={newHead.code} onChange={e => setNewHead(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="e.g. 1E, 1F, A" autoFocus className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white text-sm outline-none focus:border-violet-500 transition placeholder-slate-600" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5">Sub-Head Name *</label>
                <input type="text" value={newHead.name} onChange={e => setNewHead(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Details..." className="w-full bg-slate-950 border border-slate-700 p-3 rounded-xl text-white text-sm outline-none focus:border-violet-500 transition placeholder-slate-600" />
              </div>
              <div className="flex gap-3 pt-2 border-t border-slate-800">
                <button onClick={() => setAddHeadModal(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-xl transition text-sm">Cancel</button>
                <button onClick={addSubHead} className="flex-1 bg-violet-600 hover:bg-violet-500 text-white font-bold py-3 rounded-xl transition text-sm">Add Sub-Head</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE SUB-HEAD CONFIRM */}
      {deleteHeadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="bg-rose-500/10 p-5 flex items-center gap-4 border-b border-rose-500/20">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>
              <div>
                <h3 className="text-rose-400 font-bold text-lg">Remove Sub-Head?</h3>
                <p className="text-slate-400 text-xs">All rows under this sub-head will be lost</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-sm">Remove <span className="text-white font-bold">"{deleteHeadModal.name}"</span>?</p>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setDeleteHeadModal(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 py-2.5 rounded-xl text-sm font-bold transition">Cancel</button>
                <button onClick={removeSubHead} className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl text-sm font-bold transition">Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MAIN SECTION CONFIRM */}
      {deleteMainSectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden">
            <div className="bg-rose-500/10 p-5 flex items-center gap-4 border-b border-rose-500/20">
              <div className="w-10 h-10 rounded-full bg-rose-500/20 flex items-center justify-center text-rose-400">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
              </div>
              <div>
                <h3 className="text-rose-400 font-bold text-lg">Delete Main Section?</h3>
                <p className="text-slate-400 text-xs">All sub-heads and rows will be deleted</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-gray-300 text-sm">Delete <span className="text-white font-bold">"{deleteMainSectionModal.title}"</span>?</p>
              <div className="flex gap-3 mt-5">
                <button onClick={() => setDeleteMainSectionModal(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 py-2.5 rounded-xl text-sm font-bold transition">Cancel</button>
                <button onClick={removeMainSection} className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl text-sm font-bold transition">Delete</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DELETE ROW CONFIRM */}
      {confirmDeleteRow && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-slate-900 border border-rose-500/30 rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <p className="text-gray-300 text-sm mb-4">Delete this row?</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirmDeleteRow(null)} className="flex-1 bg-slate-800 hover:bg-slate-700 text-gray-300 py-2.5 rounded-xl text-sm font-bold transition">Cancel</button>
              <button onClick={removeRow} className="flex-1 bg-rose-500 hover:bg-rose-400 text-white py-2.5 rounded-xl text-sm font-bold transition">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CostOfProject;
